import type { SuperAdminShopSettingFieldDto } from "@babackapp/private-api-client/types";
import { ChevronRight, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { ConfigurationSection } from "./shop-account-sections";

/** Groups of the merchant's settings, and where Returns edits them. */
const SETTINGS_GROUPS: Array<{
  key: string;
  title: string;
  returnsPath: string;
}> = [
  {
    key: "preferences",
    title: "Préférences",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "exchange",
    title: "Échanges",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "discount",
    title: "Avoirs",
    returnsPath: "settings/customer-portal",
  },
  { key: "giftCode", title: "Bons cadeaux", returnsPath: "gift-codes" },
  {
    key: "refund",
    title: "Remboursement",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "returnToStore",
    title: "Retour en boutique",
    returnsPath: "settings/customer-portal",
  },
  {
    key: "workflowLogistician",
    title: "Workflow logisticien",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "trackingPreferences",
    title: "Tracking",
    returnsPath: "settings/shop-informations",
  },
  {
    key: "storeShippingSettings",
    title: "Expédition en boutique",
    returnsPath: "settings/shop-informations",
  },
  { key: "admin", title: "Admin Baback", returnsPath: "" },
];

const LONG_TEXT = 160;

function parse(json: string | null | undefined): unknown {
  if (json === null || json === undefined) return null;
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return json;
  }
}

function Value({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span
        className={[
          "rounded-full px-1.5 py-0.5 text-[11px] font-medium",
          value
            ? "bg-green-100 text-green-700"
            : "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        {value ? "Oui" : "Non"}
      </span>
    );
  }
  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return (
    <span className="break-all font-mono text-xs" title={text}>
      {text.length > LONG_TEXT ? `${text.slice(0, LONG_TEXT)}…` : text}
    </span>
  );
}

/** What applies when the shop holds nothing for the field. */
function Fallback({ field }: { field: SuperAdminShopSettingFieldDto }) {
  if (field.defaultJson !== null) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        défaut : <Value value={parse(field.defaultJson)} />
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">non réglé</span>;
}

function FieldRow({
  field,
  isBabackOption,
}: {
  field: SuperAdminShopSettingFieldDto;
  isBabackOption: boolean;
}) {
  const typeLabel = `${field.type}${field.isList ? "[]" : ""}${
    field.enumValues ? ` : ${field.enumValues.join(" | ")}` : ""
  }`;

  return (
    <div className="flex gap-4 py-1.5">
      <dt className="w-80 shrink-0">
        <span
          className={`font-mono text-xs ${field.isStored ? "text-foreground" : "text-muted-foreground"}`}
          title={typeLabel}
        >
          {field.path}
        </span>
        {isBabackOption && (
          <span
            className="ml-2 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
            title="Modifiable dans la section Options Baback, plus haut"
          >
            Options Baback
          </span>
        )}
        {field.description && (
          <p
            className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground"
            title={field.description}
          >
            {field.description}
          </p>
        )}
      </dt>
      <dd className="min-w-0 text-sm">
        {field.isStored ? (
          <Value value={parse(field.valueJson)} />
        ) : (
          <Fallback field={field} />
        )}
      </dd>
    </div>
  );
}

/**
 * The merchant's own settings, read only (BBS-406): Returns edits them,
 * through the services that validate them. Every field the schema declares
 * is listed, set or not; an unset one shows its default. Secrets come masked.
 */
export function ShopSettingsSection({
  shopId,
  settingFields,
  babackOptionKeys,
}: {
  shopId: string;
  settingFields: SuperAdminShopSettingFieldDto[];
  /** `group.path` of the fields editable in "Options Baback". */
  babackOptionKeys: string[];
}) {
  const optionKeys = useMemo(
    () => new Set(babackOptionKeys),
    [babackOptionKeys],
  );
  const [isStoredOnly, setIsStoredOnly] = useState(false);
  const byGroup = useMemo(() => {
    const groups = new Map<string, SuperAdminShopSettingFieldDto[]>();
    for (const field of settingFields) {
      groups.set(field.group, [...(groups.get(field.group) ?? []), field]);
    }
    return groups;
  }, [settingFields]);

  return (
    <ConfigurationSection
      title="Réglages du marchand (lecture seule)"
      description="Tous les champs prévus, réglés ou non : un champ non réglé affiche sa valeur par défaut. Ils se modifient dans Returns, sauf ceux marqués Options Baback ; les jetons et clés sont masqués."
    >
      <label className="flex items-center gap-2 border-b px-5 py-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={isStoredOnly}
          onChange={(event) => setIsStoredOnly(event.target.checked)}
          className="h-4 w-4 accent-violet-600"
        />
        Seulement les champs réglés
      </label>
      <div className="divide-y">
        {SETTINGS_GROUPS.map(({ key, title, returnsPath }) => {
          const fields = byGroup.get(key) ?? [];
          const storedCount = fields.filter(({ isStored }) => isStored).length;
          const shown = isStoredOnly
            ? fields.filter(({ isStored }) => isStored)
            : fields;
          return (
            <details key={key} className="group px-5 py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                  {title}
                  <span className="text-xs font-normal text-muted-foreground">
                    {storedCount} réglé{storedCount > 1 ? "s" : ""} sur{" "}
                    {fields.length}
                  </span>
                </span>
                {returnsPath && (
                  <a
                    href={`/shops/${shopId}/${returnsPath}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-violet-700 hover:underline"
                  >
                    Modifier dans Returns
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </summary>
              <dl className="mt-3 divide-y">
                {shown.length === 0 ? (
                  <p className="py-1.5 text-xs text-muted-foreground">
                    Aucun champ réglé
                  </p>
                ) : (
                  shown.map((field) => (
                    <FieldRow
                      key={field.path}
                      field={field}
                      isBabackOption={optionKeys.has(
                        `${field.group}.${field.path}`,
                      )}
                    />
                  ))
                )}
              </dl>
            </details>
          );
        })}
      </div>
    </ConfigurationSection>
  );
}
